import {
  Circle,
  KeyboardArrowDownOutlined,
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import {
  amber,
  deepPurple,
  green,
  grey,
  orange,
  red,
} from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypesBoolean } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { sidebarSetValues } from "../../../../redux/actions/sidebarActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import AgGridTableWrapper from "../../../agGrid/AgGridTableWrapper";
import ObjectInfoTooltip from "../../../data/ObjectInfoTooltip";
import Flx from "../../../layout/Flx";
import Txt from "../../../typography/Txt";
import LoanFileCommentsBadge from "../LoanFileCommentsBadge";
import {
  selectOptionsDocumentStatus,
  documentStatusLabel,
} from "../../../../constants/selectOptions/selectOptionsDocumentStatus";
import CellRendererLoanFileStatus from "../../../agGrid/cellRenderer/CellRendererLoanFileStatus";
import SideCardModal from "../../../modals/SideCardModal";
import LoanFilePreview from "../../../../screens/LoanDrilldownScreen/tabs/LoanDrilldownFilesTab/LoanFilePreview";

const VIEW_TYPES = {
  COMPACT: "compact",
  STANDARD: "standard",
};
const GROUP_MODE_TYPES = {
  DOC_GROUP: "docGroup",
  DOC_TYPE: "docType",
};
const LoanFilesDisplayTable = ({
  quickFilter,
  rowData,
  height,
  view = VIEW_TYPES.STANDARD,
  onRowClicked,
  sx,
  allowEdit = false,
  headerHeight = 0,
  groupMode,
  showDocTypeColumn = false,
  domLayout = "autoHeight",
}) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const sidebarType = useSelector((state) => state.sidebar?.type);
  const [file, setFile] = useState(null);
  /**
   * COLUMNS
   */
  const columnTypes = useMemo(() => {
    return {
      ...columnTypesBoolean(),
      ...columnTypesDate(),
    };
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        field: "file_display_name",
        flex: 1,
        headerName: "File Name",
        minWidth: 250,
        autoHeight: true,
        tooltipValueGetter: (params) => `Preview File`,
        hide: false,
        cellStyle: { paddingRight: 0, paddingLeft: groupMode ? 36 : 10 },
        cellRenderer: (params) => {
          if (view === VIEW_TYPES.STANDARD) {
            return params?.value;
          }
          const getStatusStyles = (p) => {
            const status = p?.data?.documentStatus;

            if (status === "Rejected" || status === "Incomplete") {
              return {
                // borderRadius: "40px",
                // px: 0.6,
                // background: "rgba(175, 76, 78, 0.1)",
                ".txt": {
                  color: red[800],

                  // fontWeight: 600,
                },
              };
            }

            if (status === "Pending Review") {
              return {
                // borderRadius: "40px",
                // px: 0.6,
                // background: "rgb(255, 248, 225)",
                ".txt": {
                  color: orange[900],
                  // color: grey[900],

                  // fontWeight: 600,
                },
              };
            }
            if (status === "Accepted") {
              return {
                // borderRadius: "40px",
                // px: 0.6,
                // background: "rgba(76, 175, 80, 0.1)",
                ".txt": {
                  color: green[800],

                  // fontWeight: 600,
                },
              };
            }
          };
          return (
            <Flx
              fw
              jb
              ac
              sx={{
                py: 1.5,
                // py: 1,
                // px: 0.7,
              }}
            >
              {/* <Flx column gap={0.3} sx={{ minWidth: 0, flex: 1 }}> */}
              <Flx column gap={0.1} sx={{ minWidth: 0, flex: 1 }}>
                <Txt
                  fontSize={"12px"}
                  // fontWeight={500}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.value}
                </Txt>
                <Flx ac wrap g={0.8}>
                  <Tooltip title="Status">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.3,
                        ...getStatusStyles(params),
                      }}
                    >
                      <Txt sx={{ fontSize: "10.5px" }} secondary>
                        {documentStatusLabel(params?.data?.documentStatus)}
                      </Txt>
                    </Box>
                  </Tooltip>
                  <Circle sx={{ width: 4, height: 4, color: grey[700] }} />
                  <Tooltip title="Upload Date">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                    >
                      <Txt sx={{ fontSize: "10.5px" }} secondary>
                        {formatDateShort(params?.data?.uploadDate)}
                      </Txt>
                    </Box>
                  </Tooltip>
                  <Circle sx={{ width: 4, height: 4, color: grey[700] }} />
                  <LoanFileCommentsBadge
                    showZero
                    file={params.data}
                    countButton
                    sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                  />
                </Flx>
              </Flx>
            </Flx>
          );
        },
      },
      {
        field: "docType",
        headerName: "Document Type",
        // hide: !showDocTypeColumn,
        sort: groupMode === GROUP_MODE_TYPES.DOC_TYPE ? "asc" : undefined,
        rowGroup: groupMode === GROUP_MODE_TYPES.DOC_TYPE,
      },

      {
        headerName: "Status",
        field: "documentStatus",

        // rowGroup: groupMode === GROUP_MODE_TYPES.DOC_TYPE,
        // rowGroup: groupByStatus,

        // sort: sortFilter?.colId === "documentStatus" ? sortFilter?.sort : null,
        valueGetter: (params) => {
          const value = params.data?.documentStatus;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        minWidth: 160,
        width: 160,
        flex: 0,
        // Custom comparator for sorting in specific order
        comparator: (valueA, valueB) => {
          const statusOrder = [
            "New Upload",
            "Pending Review",
            "Processor Reviewed",
            "Incomplete",
            "Rejected",
            "Accepted",
          ];

          const indexA = statusOrder.indexOf(valueA);
          const indexB = statusOrder.indexOf(valueB);

          // Handle values not in the predefined order (put them at the end)
          const finalIndexA = indexA === -1 ? statusOrder.length : indexA;
          const finalIndexB = indexB === -1 ? statusOrder.length : indexB;

          return finalIndexA - finalIndexB;
        },
        cellEditorParams: {
          values: selectOptionsDocumentStatus || [],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          valueListWidth: 620,
        },
        cellClass: "ag-cell-vertical-center",
        cellStyle: (params) => {
          const status = params?.data?.documentStatus;
          const baseStyles = {
            color: "inherit",
            background: null,
            borderLeft: null,
          };

          if (
            status === "Initial Reviewed" ||
            status === "Legal Reviewed" ||
            status === "Pending Review"
          ) {
            baseStyles.background = amber[50];
            baseStyles.borderLeft = `2px solid ${orange[100]}`;
          }
          if (status === "Processor Reviewed") {
            baseStyles.background = deepPurple[50];
            baseStyles.borderLeft = `2px solid ${deepPurple[100]}`;
          }
          if (
            status === "Expired" ||
            status === "Rejected" ||
            status === "Incomplete"
          ) {
            baseStyles.background = red[50];
            baseStyles.color = red[900];
            baseStyles.borderLeft = `2px solid ${red[100]}`;
          }

          if (status === "Accepted") {
            baseStyles.color = green[900];
            baseStyles.background = green[50];
            baseStyles.borderLeft = `2px solid ${green[100]}`;
          }
          return baseStyles;
        },

        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererLoanFileStatus params={params} />
        ),
      },
      {
        flex: 0,
        width: headerHeight === 0 ? 60 : 72,
        headerName: "Details",
        cellStyle: {
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          paddingRight: 5,
          paddingLeft: 5,
        },
        cellRenderer: (params) => {
          return (
            <Flx ac>
              {/* {allowEdit && <FileOptions params={params} />} */}
              {/* <FileOptions params={params} /> */}
              <ObjectInfoTooltip
                title="File Details"
                format={[
                  { field: "modifiedDate", type: "date" },
                  { field: "uploadDate", type: "date" },
                  { field: "reportEffectiveDate", type: "date" },
                  { field: "uploadDate", type: "date" },
                ]}
                data={params?.data}
                excludeKeys={[
                  "_id",
                  "content_type",
                  "container_type",
                  "dealId",
                  "file_storage_name",
                  "containerName",
                ]}
              />
            </Flx>
          );
        },
        hide: false,
      },
    ],
    [headerHeight, groupMode, allowEdit, view],
  );

  const defaultColDef = useMemo(
    () => ({
      filter: true,
      // enableRowGroup: groupDocGroups || groupByDocType,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      hide: view === VIEW_TYPES.COMPACT,
      // minWidth: 200,
    }),
    [view],
  );

  const onCellClicked = useCallback(
    (params) => {
      const colDef = params?.colDef?.field;
      if (colDef !== "file_display_name") {
        return;
      }
      const fileData = params?.data;
      if (isEmpty(fileData)) {
        return;
      }
      if (sidebarType === "uwChecklist") {
        dispatch(filePreviewSetData(fileData));
        return;
      }

      dispatch(
        sidebarSetValues({
          type: "filePreview",
          state: fileData,
        }),
      );
    },
    [dispatch, sidebarType],
  );

  const autoGroupColumnDef = useMemo(() => {
    return {
      field: "docGroup",
      headerName: "Document Group",
      minWidth: 220,
      cellRendererParams: {
        suppressCount: true,
      },
      cellStyle: { fontWeight: "bold", background: "red" },
      comparator: (valueA, valueB) => {
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      },
    };
  }, []);

  const handleOnRowClicked = useCallback(
    (props) => {
      if (onRowClicked) {
        onRowClicked(props);
        return;
      }
      setFile(props?.data);
    },
    [onRowClicked],
  );

  const getRowStyle = useCallback((params) => {
    if (params.node.group) {
      return { fontWeight: 700 };
    }
    return null;
  }, []);
  if (isEmpty(rowData)) {
    return (
      <Box sx={{ p: 2 }}>
        <Txt secondary>No files uploaded</Txt>
      </Box>
    );
  }

  return (
    <>
      <AgGridTableWrapper
        compactTable
        sx={sx}
        height={height}
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          columnTypes={columnTypes}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={1}
          getRowStyle={getRowStyle}
          groupRowRenderer={GroupRowRenderer}
          groupDisplayType={"groupRows"}
          //  groupDefaultExpanded={
          //               groupMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY_AND_TYPE
          //                 ? 1
          //                 : 0
          //             }
          headerHeight={headerHeight}
          tooltipShowDelay={200}
          onCellClicked={onCellClicked}
          onRowClicked={handleOnRowClicked}
          domLayout={domLayout}
          enableCellTextSelection
        />
      </AgGridTableWrapper>
      <SideCardModal
        title={file?.file_display_name || "File Preview"}
        show={!isNil(file)}
        onClose={() => setFile(null)}
        suppressBodyPadding
      >
        <LoanFilePreview
          fileObject={file}
          // loan={loanDrilldown}
          containerId={file?.containerName}
          // sx={{ overflowY: "auto" }}
        />
      </SideCardModal>
    </>
  );
};

const GroupRowRenderer = (params) => {
  const expanded = params.node.expanded;

  const getGroupLabel = () => {
    const value =
      isNil(params.value) || params.value === ""
        ? "Uncategorized"
        : params.value;
    return value;
  };

  return (
    <Flx
      ac
      gap={0.5}
      sx={{
        height: "100%",
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        pl: params.node.level * 2 + 1.5,
      }}
    >
      {expanded ? (
        <KeyboardArrowDownOutlined className="thin" />
      ) : (
        <KeyboardArrowRightOutlined className="thin" />
      )}
      <Box
        component="span"
        sx={{
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {getGroupLabel()} ({params.node.allChildrenCount})
      </Box>
    </Flx>
  );
};

export default LoanFilesDisplayTable;
